
import { useState, useEffect } from 'react';
import { saveLatestValuesToSupabase } from './utils';
import { useMarginData } from './hooks/useMarginData';
import { useLeaderboardData } from './hooks/useLeaderboardData';
import { useFilterData } from './hooks/useFilterData';
import { useChartData } from './hooks/useChartData';
import { useInputHandlers } from './hooks/useInputHandlers';

export interface MarginAnalysisState {
  filterType: string;
  selectedRegion: string;
  selectedCountry: string;
  selectedAccount: string;
  selectedProduct: string;
  selectedIndustry: string;
  regions: string[];
  countries: string[];
  accounts: string[];
  products: string[];
  industries: string[];
  productsList: string[];
  bundleList: string[];
  pricePerUnit: number;
  discountRate: number;
  orderVolume: number;
  costPerUnit: number;
  revenue: number;
  cogs: number;
  grossProfit: number;
  grossProfitMargin: number;
  marginChartData: any[];
  discountChartData: any[];
  costChartData: any[];
  volumeChartData: any[];
  leaderboardData: any[];
  actualRevenue: number;
  pricePerUnitRange: { min: number; max: number };
  orderVolumeRange: { min: number; max: number };
  costPerUnitRange: { min: number; max: number };
  priceInputValue: string;
  discountInputValue: string;
  costInputValue: string;
  volumeInputValue: string;
  isTableExpanded: boolean;
  isVolumeManuallyAdjusted: boolean;
}

export interface MarginAnalysisActions {
  setFilterType: (value: string) => void;
  setSelectedRegion: (value: string) => void;
  setSelectedCountry: (value: string) => void;
  setSelectedAccount: (value: string) => void;
  setSelectedProduct: (value: string) => void;
  setSelectedIndustry: (value: string) => void;
  setIsTableExpanded: (value: boolean) => void;
  setPricePerUnit: (value: number) => void;
  setDiscountRate: (value: number) => void;
  setOrderVolume: (value: number) => void;
  setCostPerUnit: (value: number) => void;
  handleReset: () => Promise<void>;
  handlePriceInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDiscountInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleCostInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleVolumeInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleAddToBundle: () => void;
  handleRemoveFromBundle: () => void;
  handleRegionChange: (value: string) => void;
  handleCountryChange: (value: string) => void;
  handleAccountNameChange: (value: string) => void;
  handleProductLineChange: (value: string) => void;
  handleIndustryChange: (value: string) => void;
  handleFilterTypeChange: (value: "product" | "industry") => void;
}

export const useMarginAnalysis = (): [MarginAnalysisState, MarginAnalysisActions] => {
  // Filter states
  const [filterType, setFilterType] = useState("product");
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [selectedAccount, setSelectedAccount] = useState("all");
  const [selectedProduct, setSelectedProduct] = useState("all");
  const [selectedIndustry, setSelectedIndustry] = useState("all");

  // Product and bundle states
  const [productsList, setProductsList] = useState<string[]>([]);
  const [bundleList, setBundleList] = useState<string[]>([]);

  // Financial calculation states
  const [revenue, setRevenue] = useState(0);
  const [cogs, setCogs] = useState(0);
  const [grossProfit, setGrossProfit] = useState(0);
  const [grossProfitMargin, setGrossProfitMargin] = useState(0);
  const [actualRevenue, setActualRevenue] = useState(0);

  // Input value states
  const [priceInputValue, setPriceInputValue] = useState("0");
  const [discountInputValue, setDiscountInputValue] = useState("0");
  const [costInputValue, setCostInputValue] = useState("0");
  const [volumeInputValue, setVolumeInputValue] = useState("0");

  // Range states
  const [pricePerUnitRange, setPricePerUnitRange] = useState({ min: 50, max: 200 });
  const [orderVolumeRange, setOrderVolumeRange] = useState({ min: 100, max: 5000 });
  const [costPerUnitRange, setCostPerUnitRange] = useState({ min: 20, max: 100 });

  // UI states
  const [isTableExpanded, setIsTableExpanded] = useState(false);
  const [isVolumeManuallyAdjusted, setIsVolumeManuallyAdjusted] = useState(false);

  // Custom hooks
  const { pricePerUnit, setPricePerUnit, discountRate, setDiscountRate, costPerUnit, setCostPerUnit, orderVolume, setOrderVolume } = useMarginData();

  const { leaderboardData, fetchLeaderboardData } = useLeaderboardData({
    selectedRegion,
    selectedCountry,
    selectedAccount,
    selectedProduct,
    selectedIndustry,
    filterType,
    isVolumeManuallyAdjusted,
    setOrderVolume,
    setVolumeInputValue,
    setPricePerUnit,
    setPriceInputValue,
    setDiscountRate,
    setDiscountInputValue,
    setCostPerUnit,
    setCostInputValue,
    setCostPerUnitRange,
    setPricePerUnitRange,
    setOrderVolumeRange,
    setActualRevenue,
    setCogs
  });

  const { regions, countries, accounts, products, industries, fetchCountriesByRegion, fetchAccountsByRegionAndCountry, fetchProductsByFilters, fetchIndustriesByFilters, fetchFilterData } = useFilterData({
    selectedRegion,
    selectedCountry,
    selectedAccount,
    selectedProduct,
    selectedIndustry,
    filterType,
    setSelectedCountry,
    setSelectedAccount,
    setSelectedProduct,
    setSelectedIndustry
  });

  const { marginChartData, discountChartData, costChartData, volumeChartData, updateMarginChartData } = useChartData({
    pricePerUnit,
    discountRate,
    orderVolume,
    costPerUnit
  });

  const { handlePriceInputChange, handleDiscountInputChange, handleCostInputChange, handleVolumeInputChange } = useInputHandlers({
    pricePerUnitRange,
    costPerUnitRange,
    orderVolumeRange,
    setPricePerUnit,
    setDiscountRate,
    setCostPerUnit,
    setOrderVolume,
    setPriceInputValue,
    setDiscountInputValue,
    setCostInputValue,
    setVolumeInputValue,
    setCostPerUnitRange,
    setIsVolumeManuallyAdjusted
  });

  // Effects
  useEffect(() => {
    const discountedPrice = pricePerUnit * (1 - discountRate / 100);
    const calculatedRevenue = discountedPrice * orderVolume;
    const calculatedGrossProfit = actualRevenue - cogs;
    const calculatedGrossProfitMargin = actualRevenue > 0 ? calculatedGrossProfit / actualRevenue * 100 : 0;
    
    setRevenue(calculatedRevenue);
    setGrossProfit(calculatedGrossProfit);
    setGrossProfitMargin(calculatedGrossProfitMargin);
    setPriceInputValue(pricePerUnit.toString());
    setDiscountInputValue(discountRate.toString());
    setCostInputValue(costPerUnit.toString());
    setVolumeInputValue(orderVolume.toString());
    
    updateMarginChartData(calculatedGrossProfitMargin);
  }, [pricePerUnit, discountRate, orderVolume, costPerUnit, actualRevenue, cogs]);

  useEffect(() => {
    if (actualRevenue !== 0 || cogs !== 0) {
      const calculatedGrossProfit = actualRevenue - cogs;
      const calculatedGrossProfitMargin = actualRevenue > 0 ? calculatedGrossProfit / actualRevenue * 100 : 0;
      
      setGrossProfit(calculatedGrossProfit);
      setGrossProfitMargin(calculatedGrossProfitMargin);
      updateMarginChartData(calculatedGrossProfitMargin);

      saveLatestValuesToSupabase(
        pricePerUnit,
        discountRate,
        costPerUnit,
        orderVolume,
        actualRevenue,
        cogs,
        calculatedGrossProfit,
        calculatedGrossProfitMargin
      );
    }
  }, [actualRevenue, cogs]);

  useEffect(() => {
    fetchFilterData();
    fetchLeaderboardData();
  }, []);

  useEffect(() => {
    fetchLeaderboardData();
  }, [selectedRegion, selectedCountry, selectedAccount, selectedProduct, selectedIndustry, filterType]);

  useEffect(() => {
    fetchCountriesByRegion();
    fetchAccountsByRegionAndCountry();
    if (filterType === "product") {
      fetchProductsByFilters();
    } else {
      fetchIndustriesByFilters();
    }
  }, [selectedRegion]);

  useEffect(() => {
    fetchAccountsByRegionAndCountry();
    if (filterType === "product") {
      fetchProductsByFilters();
    } else {
      fetchIndustriesByFilters();
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (filterType === "product") {
      fetchProductsByFilters();
    } else {
      fetchIndustriesByFilters();
    }
  }, [selectedAccount]);

  useEffect(() => {
    if (filterType === "product") {
      fetchProductsByFilters();
    } else {
      fetchIndustriesByFilters();
    }
    setProductsList(products);
  }, [filterType, products]);

  // Action handlers
  const handleReset = async () => {
    setIsVolumeManuallyAdjusted(false);
    await fetchLeaderboardData();
  };

  const handleAddToBundle = () => {
    if (selectedProduct && selectedProduct !== "all" && !bundleList.includes(selectedProduct)) {
      setBundleList([...bundleList, selectedProduct]);
    }
  };

  const handleRemoveFromBundle = () => {
    if (selectedProduct && selectedProduct !== "all" && bundleList.includes(selectedProduct)) {
      setBundleList(bundleList.filter(product => product !== selectedProduct));
    }
  };

  const handleRegionChange = (value: string) => {
    setSelectedRegion(value);
  };

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value);
  };

  const handleAccountNameChange = (value: string) => {
    setSelectedAccount(value);
  };

  const handleProductLineChange = (value: string) => {
    setSelectedProduct(value);
  };

  const handleIndustryChange = (value: string) => {
    setSelectedIndustry(value);
  };

  const handleFilterTypeChange = (value: "product" | "industry") => {
    setFilterType(value);
  };

  const state: MarginAnalysisState = {
    filterType,
    selectedRegion,
    selectedCountry,
    selectedAccount,
    selectedProduct,
    selectedIndustry,
    regions,
    countries,
    accounts,
    products,
    industries,
    productsList,
    bundleList,
    pricePerUnit,
    discountRate,
    orderVolume,
    costPerUnit,
    revenue,
    cogs,
    grossProfit,
    grossProfitMargin,
    marginChartData,
    discountChartData,
    costChartData,
    volumeChartData,
    leaderboardData,
    actualRevenue,
    pricePerUnitRange,
    orderVolumeRange,
    costPerUnitRange,
    priceInputValue,
    discountInputValue,
    costInputValue,
    volumeInputValue,
    isTableExpanded,
    isVolumeManuallyAdjusted
  };

  const actions: MarginAnalysisActions = {
    setFilterType,
    setSelectedRegion,
    setSelectedCountry,
    setSelectedAccount,
    setSelectedProduct,
    setSelectedIndustry,
    setIsTableExpanded,
    setPricePerUnit,
    setDiscountRate,
    setOrderVolume,
    setCostPerUnit,
    handleReset,
    handlePriceInputChange,
    handleDiscountInputChange,
    handleCostInputChange,
    handleVolumeInputChange,
    handleAddToBundle,
    handleRemoveFromBundle,
    handleRegionChange,
    handleCountryChange,
    handleAccountNameChange,
    handleProductLineChange,
    handleIndustryChange,
    handleFilterTypeChange
  };

  return [state, actions];
};
